import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deletePostCall,
  fetchPostsByTypeCall,
  fetchUserAllPostsCall,
  fetchUserLikedPostsCall,
  likeOrUnlikePostCall,
  uploadPostCall,
} from "../api/postApiCalls";
import { searchBarCall } from "../api/generalApiCalls";
import { IgPostDetailsDTO } from "../types/postDTOs";
import { useUser } from "../context/UserContext";
import { useRefreshMyProfile } from "./useUsers";
import { UploadFile } from "../api/client";

// One page of gallery items. Replaces the old hard-coded count: 1000 fetches.
export const GALLERY_PAGE_SIZE = 24;

export const usePostsByType = (type: string) =>
  useInfiniteQuery({
    queryKey: ["posts", "by-type", type],
    queryFn: ({ pageParam }) =>
      fetchPostsByTypeCall(type, pageParam, GALLERY_PAGE_SIZE).then(
        (r) => r.data
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === GALLERY_PAGE_SIZE ? allPages.length : undefined,
  });

export const useArtistPosts = (id: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ["posts", "artist", id],
    queryFn: ({ pageParam }) =>
      fetchUserAllPostsCall(id, pageParam, GALLERY_PAGE_SIZE).then(
        (r) => r.data
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === GALLERY_PAGE_SIZE ? allPages.length : undefined,
    enabled: enabled && id !== "0" && id !== "",
  });

export const useUserLikedPosts = (id: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ["posts", "liked", id],
    queryFn: ({ pageParam }) =>
      fetchUserLikedPostsCall(id, pageParam, GALLERY_PAGE_SIZE).then(
        (r) => r.data
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === GALLERY_PAGE_SIZE ? allPages.length : undefined,
    enabled: enabled && id !== "0" && id !== "",
  });

export const useSearch = (query: string) =>
  useQuery({
    queryKey: ["search", query],
    queryFn: () => searchBarCall(query, 0, GALLERY_PAGE_SIZE).then((r) => r.data),
    enabled: query.length > 0,
  });

type LikeAction = "like" | "unlike";

/** Returns the post with the current user added to / removed from its likes. */
const applyLike = (
  post: IgPostDetailsDTO,
  userId: number,
  action: LikeAction
): IgPostDetailsDTO => {
  const liked = post.likes.includes(userId);
  if (action === "like" && liked) return post;
  if (action === "unlike" && !liked) return post;
  return {
    ...post,
    likes:
      action === "like"
        ? [...post.likes, userId]
        : post.likes.filter((id) => id !== userId),
  };
};

const patchPost = (
  post: IgPostDetailsDTO,
  postId: number,
  userId: number,
  action: LikeAction
) => (post.id === postId ? applyLike(post, userId, action) : post);

// The gallery queries are infinite ({ pages: Post[][] }); search is { posts, users }.
type InfinitePosts = { pages: IgPostDetailsDTO[][]; pageParams: unknown[] };
type SearchData = { posts: IgPostDetailsDTO[]; users: unknown[] };

const patchInfinite = (
  data: InfinitePosts | undefined,
  postId: number,
  userId: number,
  action: LikeAction
) =>
  !data?.pages
    ? data
    : {
        ...data,
        pages: data.pages.map((page) =>
          page.map((post) => patchPost(post, postId, userId, action))
        ),
      };

const patchSearch = (
  data: SearchData | undefined,
  postId: number,
  userId: number,
  action: LikeAction
) =>
  !data?.posts
    ? data
    : {
        ...data,
        posts: data.posts.map((post) => patchPost(post, postId, userId, action)),
      };

/**
 * Like/unlike with an optimistic cache update. Waiting for the round trip and then
 * invalidating every post query made the heart and the count lag behind the tap, and
 * refetching the whole gallery re-rendered every tile. The cache is patched immediately,
 * rolled back if the request fails, and revalidated once it settles.
 */
export const useLikePost = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: ({ postId, action }: { postId: number; action: LikeAction }) =>
      likeOrUnlikePostCall(postId, action),

    onMutate: async ({ postId, action }) => {
      // Stop in-flight refetches from overwriting the optimistic value.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["posts"] }),
        queryClient.cancelQueries({ queryKey: ["search"] }),
      ]);

      const previous = [
        ...queryClient.getQueriesData({ queryKey: ["posts"] }),
        ...queryClient.getQueriesData({ queryKey: ["search"] }),
      ];

      queryClient.setQueriesData<InfinitePosts>(
        { queryKey: ["posts"] },
        (data) => patchInfinite(data, postId, user.id, action)
      );
      queryClient.setQueriesData<SearchData>(
        { queryKey: ["search"] },
        (data) => patchSearch(data, postId, user.id, action)
      );

      return { previous };
    },

    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData(key, data)
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.invalidateQueries({ queryKey: ["search"] });
    },
  });
};

// Upload one or more portfolio images, then refresh post lists so they appear immediately.
export const useUploadPost = () => {
  const queryClient = useQueryClient();
  const refreshProfile = useRefreshMyProfile();
  return useMutation({
    mutationFn: ({
      images,
      captions,
    }: {
      images: UploadFile[];
      captions: string[];
    }) => uploadPostCall(images, captions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      refreshProfile();
    },
  });
};

// Max images per upload — mirrors the backend MAX_UPLOAD_BATCH.
export const MAX_UPLOAD_BATCH = 10;

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  const refreshProfile = useRefreshMyProfile();
  return useMutation({
    mutationFn: (postId: number) => deletePostCall(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      refreshProfile();
    },
  });
};
